import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Write a single entry to the `admin_activity_logs` collection.
 *
 * This is the audit trail: who did what, to what. Reads are never
 * logged — only mutations (create/update/delete) and auth events
 * (login/logout). The Admin Activity page (master-admin only) renders
 * these entries in reverse-chronological order.
 *
 * Callers should keep `action` in a stable snake_case verb form so the
 * filters on the page work by exact match (e.g. 'block_number',
 * 'cancel_order'). `targetLabel` is the human-readable ID for display
 * (e.g. order number, phone number, driver name).
 *
 * `actor` is the currently-authenticated admin object from useAuth().
 * Passing it in explicitly (vs reading from a hook here) keeps this
 * helper usable from any code path — providers, event handlers, etc.
 */
export async function logAdminActivity({
  actor,
  action,
  targetType = null,
  targetId = null,
  targetLabel = null,
}) {
  // Refuse to log when we don't know who did it. Better to silently
  // drop than write an entry with actorUid=null that pollutes the audit
  // trail — the caller most likely forgot to pass `actor`.
  if (!actor || !actor.uid) {
    console.warn('[adminActivityLog] skipping — no actor context', { action });
    return;
  }
  if (!action) {
    console.warn('[adminActivityLog] skipping — no action name');
    return;
  }
  try {
    await addDoc(collection(db, 'admin_activity_logs'), {
      actorUid: actor.uid,
      actorName: actor.name || actor.email || 'unknown',
      actorEmail: actor.email || null,
      actorType: actor.type || 'unknown', // admin_master|admin|operator
      action,
      targetType,
      targetId,
      targetLabel,
      createdAt: serverTimestamp(),
    });
  } catch (e) {
    // Never let audit-log failures break the actual admin action —
    // that would be a worse UX than a missing log entry.
    console.error('[adminActivityLog] write failed', e, { action, targetType, targetId });
  }
}
